import { io, type Socket } from "socket.io-client";
import { videoOptions } from "@/config";
import type { MediaKind } from "mediasoup-client/types";
import type { JoinRoomResult } from "@/types/joinRoomResult";
import type { TransportOptions } from "mediasoup-client/types";
import type { CameraState } from "@/enums/CameraState";

type Ack<TResult> = { ok: true; result: TResult } | { ok: false; error: string };

export function useVideoHub(videoId: string) {
  let socket: Socket | null = null;

  function request<TResult>(eventName: string, ...args: unknown[]): Promise<TResult> {
    return new Promise<TResult>((resolve, reject) => {
      if (!socket) {
        reject(new Error("The video signalling connection is not started."));
        return;
      }

      socket.emit(eventName, ...args, (response: Ack<TResult>) => {
        if (response?.ok) {
          resolve(response.result);
        } else {
          reject(new Error(response?.error ?? `${eventName} failed.`));
        }
      });
    });
  }

  async function startConnection(): Promise<Socket> {
    if (socket?.connected) {
      return socket;
    }

    socket = io(videoOptions.VIDEO_SERVER_URL, {
      path: "/video-signalling",
      transports: ["websocket"],
      auth: { videoId },
    });

    await new Promise<void>((resolve, reject) => {
      socket!.once("connect", () => resolve());
      socket!.once("connect_error", (error: Error) => reject(error));
    });

    return socket;
  }

  async function joinRoom(): Promise<JoinRoomResult | null> {
    return await request<JoinRoomResult>("joinRoom");
  }

  async function createWebRtcTransport(): Promise<TransportOptions> {
    return await request<TransportOptions>("createWebRtcTransport");
  }

  async function connectWebRtcTransport(dtlsParameters: any) {
    return await request("connectWebRtcTransport", dtlsParameters);
  }

  async function produce(kind: MediaKind, rtpParameters: any): Promise<string | null> {
    return await request<string>("produce", kind, rtpParameters);
  }

  async function reportCameraState(cameraState: CameraState): Promise<void> {
    await request("reportCameraState", cameraState);
  }

  async function reportTorchState(available: boolean, on: boolean): Promise<void> {
    await request("reportTorchState", available, on);
  }

  async function reportCameraSwitchFailed(): Promise<void> {
    await request("reportCameraSwitchFailed");
  }

  async function reportTorchToggleFailed(): Promise<void> {
    await request("reportTorchToggleFailed");
  }

  async function sendProducerStats(stats: any): Promise<void> {
    await request("sendProducerStats", stats);
  }

  async function disconnect() {
    socket?.disconnect();
    socket = null;
  }

  return {
    startConnection,
    joinRoom,
    createWebRtcTransport,
    connectWebRtcTransport,
    produce,
    reportCameraState,
    reportTorchState,
    reportCameraSwitchFailed,
    reportTorchToggleFailed,
    sendProducerStats,
    disconnect
  };
}
